/**
 * /clinician/readmission-risk — deep-dive page for the Readmission Risk Engine.
 *
 * Lists EVERY patient on the visiting clinician's roster (not just the top-3
 * the dashboard widget surfaces), with NEWS2 trend sparkline, LACE score
 * breakdown, and inline CTAs for vitals + LACE recompute. Each row drills
 * into the same VitalsRecordModal + LaceComputeModal used on the dashboard.
 *
 * Honest framing: NEWS2 (Royal College of Physicians) + LACE (van Walraven
 * CMAJ 2010) are public, validated indices. Surfaced as decision support;
 * the engine never autonomously changes care plans.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Activity, ClipboardList, ArrowLeft, ShieldAlert, Stethoscope } from 'lucide-react';
import VitalsRecordModal from '../components/brand/VitalsRecordModal';
import LaceComputeModal from '../components/brand/LaceComputeModal';
import { Seo } from '../components/seo/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_STYLE = {
  high: { cls: 'border-red-500/40 bg-red-500/10 text-red-600', label: 'High' },
  moderate: { cls: 'border-cn-accent/40 bg-cn-accent/10 text-cn-accent', label: 'Moderate' },
  low: { cls: 'border-cn-success/40 bg-cn-success/10 text-cn-success', label: 'Low' },
};

const ClinicianReadmissionRisk = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roster, setRoster] = useState([]);
  const [counts, setCounts] = useState({ total: 0, high: 0 });
  const [err, setErr] = useState(null);
  const [vitalsTarget, setVitalsTarget] = useState(null);
  const [laceTarget, setLaceTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/clinician/readmission/roster`, getAuthHeader?.());
      setRoster(r.data?.roster || []);
      setCounts({ total: r.data?.count || 0, high: r.data?.high_count || 0 });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Unable to load roster.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const bandPill = (band) => {
    const s = BAND_STYLE[band];
    if (!s) {
      return (
        <span className="text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded border border-cn-border bg-cn-bg/60 text-cn-text-muted">
          Unscored
        </span>
      );
    }
    return (
      <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded border ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="readmission-risk-page">
      <Seo title="Readmission Risk · CaraLoom" description="NEWS2 + LACE 30-day readmission watchlist for visiting clinicians."
           noindex />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/clinician/dashboard" className="inline-flex items-center gap-1.5 text-sm text-cn-text-muted hover:text-cn-primary mb-6"
              data-testid="readmission-back-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Readmission Risk Engine</p>
            <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-cn-text dark:text-white mt-1">
              30-day readmission watchlist
            </h1>
            <p className="text-sm text-cn-text-muted mt-2 max-w-2xl">
              NEWS2 (Royal College of Physicians) + LACE (van Walraven 2010) — validated
              indices for early-warning deterioration and 30-day readmission risk.
              Surfaced as clinical decision support; CaraLoom never autonomously changes
              care plans.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="px-4 py-3 rounded-xl border border-cn-primary/30 bg-cn-primary/8 text-center min-w-[110px]">
              <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">Monitored</p>
              <p className="text-3xl font-display font-extrabold text-cn-primary mt-1"
                 data-testid="readmission-page-total">{counts.total}</p>
            </div>
            <div className={`px-4 py-3 rounded-xl text-center min-w-[110px] ${counts.high > 0 ? 'border border-red-500/40 bg-red-500/10' : 'border border-cn-border/60 bg-cn-bg/60'}`}>
              <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">High risk</p>
              <p className={`text-3xl font-display font-extrabold mt-1 ${counts.high > 0 ? 'text-red-600' : 'text-cn-text-muted'}`}
                 data-testid="readmission-page-high">{counts.high}</p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-8 space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-cn-bg/60 border border-cn-border/60 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && err && (
          <div className="mt-8 p-6 rounded-2xl border border-red-500/40 bg-red-500/8 text-red-600">
            <p className="font-semibold">Couldn't load roster</p>
            <p className="text-sm mt-1">{err}</p>
          </div>
        )}

        {!loading && !err && roster.length === 0 && (
          <div className="mt-8 p-8 rounded-2xl border border-dashed border-cn-border/70 bg-white dark:bg-cn-ink-900"
               data-testid="readmission-page-empty">
            <div className="flex items-start gap-4 max-w-xl">
              <Stethoscope className="w-6 h-6 text-cn-primary mt-1 shrink-0" />
              <div>
                <h2 className="text-lg font-display font-extrabold text-cn-text dark:text-white">
                  Your readmission roster is empty
                </h2>
                <p className="text-sm text-cn-text-muted mt-2">
                  Once a discharge referral is accepted or a patient is assigned to your
                  panel, CaraLoom starts tracking vitals (NEWS2) and the 30-day
                  readmission index (LACE) for that patient — and surfaces the watchlist
                  here.
                </p>
              </div>
            </div>
          </div>
        )}

        {!loading && !err && roster.length > 0 && (
          <div className="mt-8 space-y-3" data-testid="readmission-page-roster">
            {roster.map((p) => (
              <div key={p.patient_id}
                   className="rounded-2xl border border-cn-border/60 bg-white dark:bg-cn-ink-900 p-5 hover:shadow-md transition-shadow"
                   data-testid={`readmission-row-${p.patient_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {p.combined_band === 'high' && <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />}
                    <div>
                      <p className="text-base font-display font-bold text-cn-text dark:text-white">
                        {p.patient_name || `Patient ${p.patient_id.slice(0, 8)}`}
                      </p>
                      {(p.primary_diagnosis || p.partner_name) && (
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          {p.partner_name && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border border-cn-primary/30 bg-cn-primary/8 text-cn-primary"
                                  data-testid={`readmission-page-partner-${p.patient_id}`}>
                              {p.partner_name}
                            </span>
                          )}
                          {p.primary_diagnosis && (
                            <p className="text-xs text-cn-text dark:text-cn-text-muted">{p.primary_diagnosis}</p>
                          )}
                        </div>
                      )}
                      <p className="text-[11px] text-cn-text-muted mt-0.5">
                        {p.last_vitals_at
                          ? `Last vitals · ${new Date(p.last_vitals_at).toLocaleString()}`
                          : 'No vitals recorded yet'}
                        {p.source === 'discharge_placement' && !p.partner_name && ' · From discharge placement'}
                      </p>
                    </div>
                  </div>
                  {bandPill(p.combined_band)}
                </div>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="px-3 py-2 rounded-lg border border-cn-border/60 bg-cn-bg/40">
                    <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">NEWS2</p>
                    <p className="text-lg font-display font-extrabold text-cn-text dark:text-white">
                      {p.last_news2_total ?? '—'}
                    </p>
                  </div>
                  <div className="px-3 py-2 rounded-lg border border-cn-border/60 bg-cn-bg/40">
                    <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">NEWS2 band</p>
                    <p className="text-sm font-semibold text-cn-text dark:text-white capitalize">
                      {p.last_news2_band || '—'}
                    </p>
                  </div>
                  <div className="px-3 py-2 rounded-lg border border-cn-border/60 bg-cn-bg/40">
                    <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">LACE</p>
                    <p className="text-lg font-display font-extrabold text-cn-text dark:text-white">
                      {p.lace?.score != null ? `${p.lace.score}/19` : '—'}
                    </p>
                  </div>
                  <div className="px-3 py-2 rounded-lg border border-cn-border/60 bg-cn-bg/40">
                    <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">LACE band</p>
                    <p className="text-sm font-semibold text-cn-text dark:text-white capitalize">
                      {p.lace?.band || '—'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => setVitalsTarget(p)}
                          className="cn-btn cn-btn-primary !py-2 !px-3 !text-xs inline-flex items-center gap-1.5"
                          data-testid={`readmission-page-record-vitals-${p.patient_id}`}>
                    <Activity className="w-3.5 h-3.5" /> Record vitals
                  </button>
                  <button onClick={() => setLaceTarget(p)}
                          className="cn-btn cn-btn-outline !py-2 !px-3 !text-xs inline-flex items-center gap-1.5"
                          data-testid={`readmission-page-compute-lace-${p.patient_id}`}>
                    <ClipboardList className="w-3.5 h-3.5" /> Update LACE
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {vitalsTarget && (
        <VitalsRecordModal
          open
          patient={vitalsTarget}
          getAuthHeader={getAuthHeader}
          onClose={() => setVitalsTarget(null)}
          onSaved={() => { setVitalsTarget(null); load(); }}
        />
      )}
      {laceTarget && (
        <LaceComputeModal
          open
          patient={laceTarget}
          getAuthHeader={getAuthHeader}
          onClose={() => setLaceTarget(null)}
          onSaved={() => { setLaceTarget(null); load(); }}
        />
      )}
    </div>
  );
};

export default ClinicianReadmissionRisk;
